import { useCallback, useEffect, useMemo, useState } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { PAGE_LIMIT, pageWindow } from "./toolCallsPagination";
import { REFRESH_INTERVAL_MS, useCachedQuery } from "./useCachedQuery";

export type { SubToolEntry } from "../lib/toolCallStatus";

// IP-100: shape of one row returned by GET /api/admin/tool-calls. Mirrors
// McpToolCallView on the backend.
export type ToolCallStatus = "ok" | "error";
export type ToolCallErrorKind =
  | "upstream_error"
  | "auth_error"
  | "rate_limited"
  | "timeout"
  | "network"
  | "tool_not_found"
  | "unknown";

export type AdminToolCall = {
  id: number;
  tenantId: string;
  ownerEmail: string | null;
  provider: string;
  integrationSlug: string;
  toolName: string;
  startedAt: string;
  durationMs: number;
  status: ToolCallStatus;
  errorKind: ToolCallErrorKind | null;
  requestArgs: string | null;
  requestBytes: number;
  responseBytes: number;
  truncated: boolean;
  requestId: string | null;
  // V41: per-sub-tool aggregates for COMPOSIO_MULTI_EXECUTE_TOOL rows.
  // Null on every other row. `subTools` is the raw JSONB text returned by
  // the backend; the page JSON.parses it for display.
  subToolCount: number | null;
  subToolSuccessCount: number | null;
  // IP-314: how many sub-tools were rejected locally as unconnected (not
  // forwarded -> non-billable). Null on non-multi-execute rows.
  subToolUnconnectedCount: number | null;
  subTools: string | null;
};

export type AdminToolCallsFilter = {
  customerEmail?: string | undefined;
  // Filter to a single tenant. Used by the per-instance dashboard panel; the
  // admin Tool Logs tab leaves it unset.
  tenantId?: string | undefined;
  status?: ToolCallStatus | undefined;
  integrationSlug?: string | undefined;
  // IP-314: only rows that locally rejected an unconnected sub-tool. Server-side
  // (paged) so it covers the full matching set, not just the loaded page.
  unconnectedOnly?: boolean | undefined;
  // Only rows started at or after this epoch-ms instant. Drives the per-instance
  // dashboard's time-window scoping; server-side so it pages the full window.
  startedAfter?: number | undefined;
};

type FetchStatus = "loading" | "ready" | "error";

const buildUrl = (filter: AdminToolCallsFilter, before: number | null): string => {
  const params = new URLSearchParams();
  params.set("limit", String(PAGE_LIMIT));
  if (filter.customerEmail) params.set("customerEmail", filter.customerEmail);
  if (filter.tenantId) params.set("tenantId", filter.tenantId);
  if (filter.status) params.set("status", filter.status);
  if (filter.integrationSlug) params.set("integrationSlug", filter.integrationSlug);
  if (filter.unconnectedOnly) params.set("unconnected", "true");
  if (filter.startedAfter != null) params.set("startedAfter", String(filter.startedAfter));
  if (before != null) params.set("before", String(before));
  return `/api/admin/tool-calls?${params.toString()}`;
};

type Result = {
  status: FetchStatus;
  // Rows on the current page only (<= PAGE_LIMIT).
  calls: ReadonlyArray<AdminToolCall>;
  error: string | null;
  isFetching: boolean;
  // Unique integration slugs seen across every loaded page, accumulated so the
  // filter dropdown stays stable as you page rather than flickering to just the
  // current page's slugs.
  integrationSlugs: ReadonlyArray<string>;
  page: number; // 1-based current page
  canPrev: boolean;
  canNext: boolean;
  pageStart: number; // 1-based index of the first row shown (0 when empty)
  pageEnd: number; // 1-based index of the last row shown (0 when empty)
  refresh: () => void;
  nextPage: () => void;
  prevPage: () => void;
};

const PERSIST_CAP = 100;

// Keyset pagination by row id, surfaced as classic prev/next pages of
// PAGE_LIMIT rows. Page 0 lives in useCachedQuery (cached and
// background-refreshed when unfiltered); paging forward fetches the next page
// via ?before=<lastIdSeen> and buffers it in extraPages, so Prev is a free
// backward index step. While we've paged beyond page 0 the background refresh
// is disabled so a tick can't shift the keyset cursors under us.
export const useAdminToolCalls = (filter: AdminToolCallsFilter): Result => {
  const [extraPages, setExtraPages] = useState<ReadonlyArray<ReadonlyArray<AdminToolCall>>>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [endReached, setEndReached] = useState(false);
  const [pagingFetch, setPagingFetch] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  // Snapshot the filter inputs we depend on, so the effect doesn't churn on
  // every render that happens to pass a new object literal.
  const emailKey = filter.customerEmail ?? "";
  const tenantKey = filter.tenantId ?? "";
  const statusKey = filter.status ?? "";
  const slugKey = filter.integrationSlug ?? "";
  const unconnectedKey = filter.unconnectedOnly ? "1" : "";
  const afterKey = filter.startedAfter != null ? String(filter.startedAfter) : "";
  // A tenant- or window-scoped query (per-instance dashboard) is never the
  // default fleet view, so it never seeds/writes the shared cache resource.
  const isDefault =
    emailKey === "" &&
    tenantKey === "" &&
    statusKey === "" &&
    slugKey === "" &&
    unconnectedKey === "" &&
    afterKey === "";

  const url = useMemo(
    () =>
      buildUrl(
        {
          customerEmail: emailKey || undefined,
          tenantId: tenantKey || undefined,
          status: (statusKey || undefined) as ToolCallStatus | undefined,
          integrationSlug: slugKey || undefined,
          unconnectedOnly: unconnectedKey === "1",
          startedAfter: afterKey ? Number(afterKey) : undefined,
        },
        null,
      ),
    [emailKey, tenantKey, statusKey, slugKey, unconnectedKey, afterKey],
  );

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<ReadonlyArray<AdminToolCall>> => {
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return (await res.json()) as ReadonlyArray<AdminToolCall>;
    },
    [url],
  );

  const paged = pageIndex > 0 || extraPages.length > 0;
  const { status, data, error, isFetching, refetch } = useCachedQuery<
    ReadonlyArray<AdminToolCall>
  >({
    cacheResource: isDefault ? ADMIN_QUERIES.toolCalls.resource : null,
    queryKey: url,
    fetcher,
    refreshIntervalMs: paged ? 0 : REFRESH_INTERVAL_MS,
    persistTransform: (calls) => calls.slice(0, PERSIST_CAP),
  });

  // Deeper pages belong to the previous query; drop them when it changes.
  useEffect(() => {
    setExtraPages([]);
    setPageIndex(0);
    setEndReached(false);
    setPageError(null);
  }, [url]);

  // Page 0 is the cached query's payload, clamped to one page (a stale
  // localStorage seed can briefly hold more than PAGE_LIMIT rows).
  const page0 = useMemo(() => (data ?? []).slice(0, PAGE_LIMIT), [data]);
  const allPages = useMemo(() => [page0, ...extraPages], [page0, extraPages]);
  const calls = allPages[pageIndex] ?? [];

  const integrationSlugs = useMemo(() => {
    const set = new Set<string>();
    for (const pg of allPages) for (const c of pg) set.add(c.integrationSlug);
    return [...set].sort();
  }, [allPages]);

  const { canPrev, canNext, start, end } = pageWindow(
    pageIndex,
    calls.length,
    extraPages.length,
    endReached,
    PAGE_LIMIT,
  );

  const refresh = useCallback((): void => {
    setExtraPages([]);
    setPageIndex(0);
    setEndReached(false);
    setPageError(null);
    void refetch();
  }, [refetch]);

  const prevPage = useCallback((): void => {
    setPageIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const nextPage = useCallback((): void => {
    // A later page is already buffered: just step forward, no fetch.
    if (pageIndex < extraPages.length) {
      setPageIndex((i) => i + 1);
      return;
    }
    if (status !== "ready" || endReached) return;
    const current = allPages[pageIndex] ?? [];
    if (current.length < PAGE_LIMIT) {
      setEndReached(true);
      return;
    }
    const lastId = current[current.length - 1]?.id;
    if (lastId == null) return;
    setPagingFetch(true);
    setPageError(null);
    void fetch(buildUrl(filter, lastId), { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`page fetch failed (${res.status})`);
        const more = (await res.json()) as ReadonlyArray<AdminToolCall>;
        if (more.length === 0) {
          setEndReached(true);
          return;
        }
        setExtraPages((prev) => [...prev, more]);
        setEndReached(more.length < PAGE_LIMIT);
        setPageIndex((i) => i + 1);
      })
      .catch((err: unknown) => {
        setPageError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setPagingFetch(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, extraPages.length, endReached, status, allPages, emailKey, tenantKey, statusKey, slugKey, unconnectedKey, afterKey]);

  return {
    status,
    calls,
    error: error ?? pageError,
    isFetching: isFetching || pagingFetch,
    integrationSlugs,
    page: pageIndex + 1,
    canPrev,
    canNext,
    pageStart: start,
    pageEnd: end,
    refresh,
    nextPage,
    prevPage,
  };
};
